/* eslint-disable react/require-default-props */
import {
  ErrorMessage,
  InputField,
  Textarea,
  TooltipWidget,
} from 'anf-core-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { refProp, textProp } from '../../../tools/customProps';
import { Form, FormGroup } from '../../Common/Form';
import { TmntText } from '../../Common';
import HelpReasonDropDown from './HelpReasonDropDown';
import FirstAndLastNameInputFields from './FirstAndLastNameInputField';
import { emailUsFormText } from './emailUsPropTypes';
import { useCustomTealiumTrigger } from '../../../hooks/useTealiumTrigger';

export default function EmailUsFormV2({
  id,
  children = undefined,
  formRef = undefined,
  invalidFields = undefined,
  formIsInvalid = false,
  label = undefined,
  onSubmit = undefined,
  text,
  selectedReason,
  selectedReasonKey,
  handleHelpReasonChange,
}) {
  const [email, setEmail] = useState(undefined);
  const [orderNum, setOrderNum] = useState(undefined);
  const [comments, setComments] = useState(undefined);
  const [firstName, setFirstName] = useState(undefined);
  const [lastName, setLastName] = useState(undefined);
  const [isTooltipOpen, setIsToolTipOpen] = useState(false);

  const analyticsData = {
    eventName: 'universal_click',
    eventData: {
      event_name: 'click',
      tealium_event: 'click',
      data_text: 'email us full',
      data_action: 'submit',
      reason_for_help: selectedReasonKey,
    },
  };

  const includeDatabridgeDatapoints = false;
  const triggerAnalytics = useCustomTealiumTrigger(analyticsData, includeDatabridgeDatapoints);

  // temp fix until design system updates, remove when
  useEffect(() => {
    const commentInput = document.getElementById('customer-email-us-form__comments');
    commentInput.setAttribute('name', 'comment');
  }, []);

  const {
    formFieldDropDown,
    formError,
    formOrderNumberTooltipContent,
    formOrderNumberToolTipLabel,
    formWhatDoYouNeedHelpWith,
    formRequiredField,
    formFieldEmailAddress,
    formFieldOrderNumber,
    formFieldDetails,

  } = text;

  const isInvalidField = (name) => invalidFields.includes(name);

  const renderFormPromptText = () => (
    <h3 className="what-do-you-need-help-with">
      <TmntText tmnt={formWhatDoYouNeedHelpWith} />
    </h3>
  );

  const renderRequiredFields = () => (
    <TmntText tmnt={formRequiredField} />
  );

  const renderNameFields = () => {
    const firstNameIsInvalid = isInvalidField('firstName');
    const lastNameIsInvalid = isInvalidField('lastName');
    const nameIsInvalid = firstNameIsInvalid || lastNameIsInvalid;

    return (
      <FirstAndLastNameInputFields
        isInvalid={nameIsInvalid}
        text={text}
        firstName={firstName}
        setFirstName={setFirstName}
        firstNameIsInvalid={firstNameIsInvalid}
        lastName={lastName}
        setLastName={setLastName}
        lastNameIsInvalid={lastNameIsInvalid}
      />
    );
  };

  const renderTooltipWidget = () => {
    const handleOnClick = () => setIsToolTipOpen(!isTooltipOpen);

    const handleOnKeyUp = (event) => {
      event.stopPropagation();
      setIsToolTipOpen(event.key !== 'Escape');
    };

    return (
      <TooltipWidget
        buttonLabelText={formOrderNumberToolTipLabel?.value}
        contentPosition="top-right"
        contentID="customer-email-us-form__order-number-tooltip"
        isOpen={isTooltipOpen}
        onClick={handleOnClick}
        onKeyUp={handleOnKeyUp}
      >
        <TmntText tmnt={formOrderNumberTooltipContent} />
      </TooltipWidget>
    );
  };

  const renderHelpReasonDropDown = () => {
    const contactReasonIsInvalid = isInvalidField('contactReason');

    return (
      <HelpReasonDropDown
        id="customer-email-us-form__help-reason-dropdown"
        isInvalid={contactReasonIsInvalid}
        onChange={handleHelpReasonChange}
        text={formFieldDropDown}
        value={selectedReason || ''}
      />
    );
  };

  const renderEmailField = () => {
    const emailIsInvalid = isInvalidField('email');
    return (
      <InputField
        autoComplete="email"
        id="customer-email-us-form__email"
        key="email"
        isInvalid={emailIsInvalid}
        isRequired
        label={formFieldEmailAddress?.label?.value}
        name="email"
        onChange={(e) => setEmail(e.target.value)}
        type="email"
        value={email}
      >
        {emailIsInvalid && formFieldEmailAddress?.error?.value && (
        <ErrorMessage variant="error">
          <span data-property={formFieldEmailAddress?.error?.key}>
            {formFieldEmailAddress?.error?.value}
          </span>
        </ErrorMessage>
        )}
      </InputField>
    );
  };

  const renderOrderNumField = () => {
    const orderNumIsInvalid = isInvalidField('orderNum');
    return (
      <InputField
        autoComplete="off"
        id="customer-email-us-form__order-number"
        classList="order-number-input-field"
        key="orderNum"
        isRequired
        isInvalid={orderNumIsInvalid}
        label={formFieldOrderNumber?.label?.value}
        name="orderNum"
        onChange={(e) => setOrderNum(e.target.value)}
        type="text"
        value={orderNum}
      >
        {renderTooltipWidget()}
        {orderNumIsInvalid && formFieldOrderNumber?.error?.value && (
        <ErrorMessage variant="error">
          <span data-property={formFieldOrderNumber?.error?.key}>
            {formFieldOrderNumber?.error?.value}
          </span>
        </ErrorMessage>
        )}
      </InputField>
    );
  };

  const renderCommentField = () => {
    const commentIsInvalid = isInvalidField('comment');
    return (
      <Textarea
        autoComplete="off"
        id="customer-email-us-form__comments"
        isInvalid={commentIsInvalid}
        isRequired
        label={formFieldDetails?.label?.value}
        name="comment"
        onChange={(e) => setComments(e.target.value)}
        value={comments}
      >
        {commentIsInvalid && formFieldDetails?.error?.value && (
        <ErrorMessage variant="error">
          <span data-property={formFieldDetails?.error?.key}>
            {formFieldDetails?.error?.value}
          </span>
        </ErrorMessage>
        )}
      </Textarea>
    );
  };

  const renderFields = () => ([
    renderNameFields(),
    renderEmailField(),
    renderOrderNumField(),
    renderCommentField(),
  ]);

  const renderFormLabel = () => (
    <span className="screen-reader-text">
      <TmntText tmnt={label} />
    </span>
  );

  const renderFormGroups = () => ([
    <FormGroup
      key="account-form-group-dropdown"
      fields={[renderFormPromptText(), renderHelpReasonDropDown()]}
    />,
    <FormGroup
      key="account-form-group"
      fields={[renderRequiredFields(), ...renderFields()]}
    />,
  ]);

  const renderFormError = () => formError && (
    <ErrorMessage id={`${id}-error-message`}>
      <TmntText tmnt={formError} />
    </ErrorMessage>
  );

  return (
    <Form
      id={id}
      label={renderFormLabel()}
      error={renderFormError()}
      formRef={formRef}
      groups={renderFormGroups()}
      isInvalid={formIsInvalid}
      onSubmit={(event) => {
        triggerAnalytics();
        onSubmit(event);
      }}
      isValidated={false}
    >
      { children }
    </Form>
  );
}

const propTypes = {
  id: PropTypes.string.isRequired,
  label: textProp.isRequired,
  formRef: refProp,
  onSubmit: PropTypes.func,
  formIsInvalid: PropTypes.bool,
  invalidFields: PropTypes.arrayOf(PropTypes.string),
  text: emailUsFormText.isRequired,
  selectedReason: PropTypes.string,
  selectedReasonKey: PropTypes.string,
  handleHelpReasonChange: PropTypes.func.isRequired,
  children: PropTypes.node,
};

EmailUsFormV2.propTypes = propTypes;
